import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import formatDrugLabel from 'common/utils/formatDrugLabel';
import { createFillLaterItemNow } from 'store/thunks/workQueueThunks';

import { FILL_LATER_CREATE_NOW_OUTCOME } from '../constants/fillLaterEligibility';
import { formatScheduledDate } from '../utils/fillLaterDates';

/**
 * "Create now" for a Fill Later item (POM-6161). Confirms, then calls POM-6150's
 * createFillLaterItemNow, which skips the scheduled date but never the ship-first rule. Every
 * outcome closes the dialog with a toast and the page is refreshed by the thunk; only a handled
 * transport error keeps the dialog open for another try.
 */
const CreateFillLaterNowDialog = ({ open, onClose, item }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick' || submitting) return;
    onClose();
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const result = await dispatch(
        createFillLaterItemNow({ fillLaterItemId: item.fillLaterItemId })
      );
      if (!result) return;

      const report = FILL_LATER_CREATE_NOW_OUTCOME[result.outcome] ?? {
        variant: 'info',
        message: ({ outcome, reason }) => `${outcome}${reason ? `: ${reason}` : ''}`,
      };
      enqueueSnackbar(report.message(result), { variant: report.variant });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='xs'
      fullWidth
      aria-labelledby='CreateFillLaterNowDialog-title'
    >
      <DialogTitle id='CreateFillLaterNowDialog-title'>Create the order now?</DialogTitle>
      <DialogContent>
        <Typography variant='body2' sx={{ mb: 0.5 }}>
          {item.patientName} · {formatDrugLabel(item)} · Qty {item.splitQuantity}
        </Typography>
        <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
          The split order is created today instead of on {formatScheduledDate(item.scheduledFor)}.
        </Typography>
        <Alert severity='warning'>
          The original line item on order {item.referenceOrderId} must already have shipped. If it
          has not, nothing is created and the item waits on that shipment instead.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <LoadingButton
          variant='contained'
          color='primary'
          loading={submitting}
          onClick={handleConfirm}
          id={`CreateFillLaterNowDialog-${item.fillLaterItemId}-confirm`}
        >
          Create now
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

CreateFillLaterNowDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    fillLaterItemId: PropTypes.string.isRequired,
    referenceOrderId: PropTypes.string,
    patientName: PropTypes.string,
    drugName: PropTypes.string,
    strength: PropTypes.string,
    splitQuantity: PropTypes.string,
    scheduledFor: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
};

export default CreateFillLaterNowDialog;
